<template>
  <div>
    <div v-if="showWidgets" class="widget-grid">
      <div
        v-for="(row, rowIndex) in resultViewWithWidgets.rows"
        :key="rowIndex"
        class="row"
      >
        <div
          v-for="(widget, colIndex) in row.cols"
          :key="colIndex"
          class="card-deck"
          :class="`col-lg-${widget.size} ${widget.type !== 'wrapper' ? 'mt-10' : ''}`"
        >
          <div
            v-if="
              allRegistrations &&
              allRegistrations[widget.id] &&
              allRegistrations[widget.id].info &&
              allRegistrations[widget.id].info.hideWidget
            "
          >
            <!-- Widget is deregistered and configured as hidden -->
          </div>
          <div
            v-else-if="allRegistrations && allRegistrations[widget.id]"
            class="h-100"
          >
            <div v-if="widgetError[widget.id] && widgetError[widget.id].error">
              <ErrorFallbackWidget
                :id="widget.id"
                :error="widgetError[widget.id]"
                :surveyDetails="surveyDetails"
                :organizationName="organizationName"
              />
            </div>
            <div
              v-else-if="
                deregisteredWidgets[widget.id] ||
                ((widget.props.loading === undefined ||
                  widget.props.loading === true) &&
                  widget.props.hideWidgetWhenLoading)
              "
              class="h-100"
            >
              <PulsSkeletonLoader
                :skeletonInfo="
                  widgetMap[widget.type].skeleton({
                    colData: widget.data,
                    widgetProps: widget.props
                  })
                "
                :deregisteredWidget="deregisteredWidgets[widget.id]"
                :hasResults="hasResults"
              />
            </div>
            <div v-else class="h-100">
              <component
                :is="widgetMap[widget.type].widget"
                v-bind="widget.props"
                :id="widget.id"
                :updateWidgetSliderState="updateWidgetSliderState"
                :widgetSliderState="widgetSliderStates[widget.id]"
                :updateWidgetRequests="updateWidgetRequests"
                :deregisterWidget="onWidgetDeregistration"
                @proposal-selected="$emit('proposal-selected', $event)"
              />
            </div>
          </div>
          <div v-else>
            <span class="text-danger">Something went wrong</span>
            <p>{{ widget.id }}: Widget not registered</p>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="widget-slide-show-wrapper">
      <WidgetSlideshow
        :deregisteredWidgets="deregisteredWidgets"
        :widgetsForSlides="widgetsForSlides"
        :selectedGroup="selectedGroup"
        :widgetRegistrations="widgetRegistrations"
        :widgetMap="widgetMap"
        :didCloseSlideShowCallback="didCloseSlideShow"
        :updateWidgetSliderState="updateWidgetSliderState"
        :widgetSliderStates="widgetSliderStates"
        :updateWidgetRequests="updateWidgetRequests"
        :deregisterWidget="onWidgetDeregistration"
      />
    </div>
  </div>
</template>

<script>
import ActionsSummaryInfoWidget, {
  actionSummaryInfoWidgetInformation,
  actionSummaryInfoWidgetSkeleton
} from '@/components/widgets/action-summary-info-widget/ActionSummaryInfoWidget.vue'

import AlertWidget, {
  alertWidgetInformation,
  alertWidgetSkeleton
} from '@/components/widgets/alert-widget/AlertWidget.vue'

import CategoriesBarsWidget, {
  categoriesBarsWidgetInformation,
  categoriesBarsWidgetSkeleton
} from '@/components/widgets/categories-bars-widget/CategoriesBarsWidget.vue'

import CategoryQuestionsWidget, {
  categoryQuestionsWidgetInformation,
  categoryQuestionsWidgetSkeleton
} from '@/components/widgets/category-questions-widget/CategoryQuestionsWidget.vue'

import GenericTextWidget, {
  genericTextWidgetInformation,
  genericTextWidgetSkeleton
} from '@/components/widgets/generic-text-widget/GenericTextWidget.vue'

import GroupActionsWidget, {
  groupActionsWidgetInformation,
  groupActionsWidgetSkeleton
} from '@/components/widgets/group-actions-widget/GroupActionsWidget.vue'

import GroupActionsLocalizableWidget, {
  groupActionsLocalizableWidgetInformation,
  groupActionsLocalizableWidgetSkeleton
} from '@/components/widgets/group-actions-localizable-widget/GroupActionsLocalizableWidget.vue'

import GroupPrioActionsWidget, {
  groupPrioActionsWidgetInformation,
  groupPrioActionsWidgetSkeleton
} from '@/components/widgets/group-prio-actions-widget/GroupPrioActionsWidget.vue'

import InsightsWidget, {
  insightsWidgetInformation,
  insightsWidgetSkeleton
} from '@/components/widgets/insights-widget/InsightsWidget.vue'

import ResultsMessagesWidget, {
  resultsMessagesWidgetInformation,
  resultsMessagesWidgetSkeleton
} from '@/components/widgets/results-messages-widget/ResultsMessagesWidget.vue'

import SurveyInfoWidget, {
  surveyInfoWidgetInformation,
  surveyInfoWidgetSkeleton
} from '@/components/widgets/survey-info-widget/SurveyInfoWidget.vue'

import TagValueWidget, {
  tagValueWidgetInformation,
  tagValueWidgetSkeleton
} from '@/components/widgets/tag-value-widget/TagValueWidget.vue'

import TagsDonutsWidget, {
  tagsDonutsWidgetInformation,
  tagsDonutsWidgetSkeleton
} from '@/components/widgets/tags-donuts-widget/TagsDonutsWidget.vue'

import TagsTrendsWidget, {
  tagsTrendsWidgetInformation,
  tagsTrendsWidgetSkeleton
} from '@/components/widgets/tags-trends-widget/TagsTrendsWidget.vue'

import WrapperWidget, {
  wrapperWidgetInformation,
  wrapperWidgetSkeleton
} from '@/components/widgets/wrapper-widget/WrapperWidget.vue'

import NotImplementedWidget from '@/components/widgets/not-implemented-widget/NotImplementedWidget.vue'
import ErrorFallbackWidget from '@/components/widgets/ErrorFallbackWidget.vue'
import PulsSkeletonLoader from '@/components/shared/PulsSkeletonLoader.vue'

import WidgetSlideshow from '@/components/results/subcomponents/WidgetSlideshow.vue'

export default {
  name: 'WidgetGrid',
  components: {
    ErrorFallbackWidget,
    NotImplementedWidget,
    PulsSkeletonLoader,
    WidgetSlideshow
  },
  props: {
    deregisteredWidgets: {
      type: Object,
      default: () => {}
    },
    getDataForRequestFromStoresCallback: {
      type: Function,
      default: () => []
    },
    hasResults: {
      type: Boolean,
      default: false
    },
    level: {
      type: Number,
      default: 0
    },
    onChangeViewMode: {
      type: Function,
      default: () => {}
    },
    onComponentError: {
      type: Function,
      default: () => {}
    },
    onWidgetDeregistration: {
      type: Function,
      default: () => {}
    },
    onWidgetRegistration: {
      type: Function,
      default: () => {}
    },
    organizationFullName: {
      type: String,
      default: ''
    },
    organizationName: {
      type: String,
      default: ''
    },
    updateWidgetSliderState: {
      type: Function,
      default: () => {}
    },
    updateWidgetRegistration: {
      type: Function,
      default: () => {}
    },
    requestCallbacks: {
      type: Object,
      default: () => {}
    },
    resultViewForUser: {
      type: Object,
      default: () => {}
    },
    shouldShowResultsMessages: {
      type: Boolean,
      default: false
    },
    selectedAttributeSegment: {
      type: Object,
      default: () => {}
    },
    selectedGroup: {
      type: Object,
      default: () => {}
    },
    surveyDetails: {
      type: Object,
      default: () => {}
    },
    filteredSurveyDetails: {
      type: Object,
      default: () => {}
    },
    surveyName: {
      type: String,
      default: ''
    },
    surveyIsAggregate: {
      type: Boolean,
      default: false
    },
    surveyIsAggregateV2: {
      type: Boolean,
      default: false
    },
    surveyContainsOnlyFreeTextQuestions: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'default'
    },
    userCanSelectAlertActions: {
      type: Boolean,
      default: false
    },
    userHasConfirmedActions: {
      type: Boolean,
      default: false
    },
    userInfo: {
      type: Object,
      default: () => {}
    },
    userIsGroupAdmin: {
      type: Boolean,
      default: false
    },
    userIsPulsAdmin: {
      type: Boolean,
      default: false
    },
    viewMode: {
      type: String,
      default: 'default'
    },
    widgetRegistrations: {
      type: Object,
      default: () => {}
    },
    widgetSliderStates: {
      type: Object,
      default: () => {}
    }
  },
  setup() {
    const widgetMap = {
      actions_summary_info: {
        widget: ActionsSummaryInfoWidget,
        information: actionSummaryInfoWidgetInformation,
        skeleton: actionSummaryInfoWidgetSkeleton
      },
      alert_box: {
        widget: AlertWidget,
        information: alertWidgetInformation,
        skeleton: alertWidgetSkeleton
      },
      categories_bars: {
        widget: CategoriesBarsWidget,
        information: categoriesBarsWidgetInformation,
        skeleton: categoriesBarsWidgetSkeleton
      },
      category_questions: {
        widget: CategoryQuestionsWidget,
        information: categoryQuestionsWidgetInformation,
        skeleton: categoryQuestionsWidgetSkeleton
      },
      generic_text: {
        widget: GenericTextWidget,
        information: genericTextWidgetInformation,
        skeleton: genericTextWidgetSkeleton
      },
      group_actions: {
        widget: GroupActionsWidget,
        information: groupActionsWidgetInformation,
        skeleton: groupActionsWidgetSkeleton
      },
      group_actions_localizable: {
        widget: GroupActionsLocalizableWidget,
        information: groupActionsLocalizableWidgetInformation,
        skeleton: groupActionsLocalizableWidgetSkeleton
      },
      group_prio_actions: {
        widget: GroupPrioActionsWidget,
        information: groupPrioActionsWidgetInformation,
        skeleton: groupPrioActionsWidgetSkeleton
      },
      insights: {
        widget: InsightsWidget,
        information: insightsWidgetInformation,
        skeleton: insightsWidgetSkeleton
      },

      results_messages: {
        widget: ResultsMessagesWidget,
        information: resultsMessagesWidgetInformation,
        skeleton: resultsMessagesWidgetSkeleton
      },
      survey_info: {
        widget: SurveyInfoWidget,
        information: surveyInfoWidgetInformation,
        skeleton: surveyInfoWidgetSkeleton
      },
      tag_value: {
        widget: TagValueWidget,
        information: tagValueWidgetInformation,
        skeleton: tagValueWidgetSkeleton
      },
      tags_donuts: {
        widget: TagsDonutsWidget,
        information: tagsDonutsWidgetInformation,
        skeleton: tagsDonutsWidgetSkeleton
      },
      tags_trends: {
        widget: TagsTrendsWidget,
        information: tagsTrendsWidgetInformation,
        skeleton: tagsTrendsWidgetSkeleton
      },
      wrapper: {
        widget: WrapperWidget,
        information: wrapperWidgetInformation,
        skeleton: wrapperWidgetSkeleton
      }
      // Map other widgets
    }
    return {
      widgetMap
    }
  },
  data() {
    return {
      widgetError: {}
    }
  },
  computed: {
    resultViewWithWidgets() {
      return {
        rows: this.resultViewForUser.rows
          .map((row, rowIndex) => {
            return {
              ...row,
              cols: row.cols.map((col, colIndex) => {
                const id = this.idForWidget({ col, rowIndex, colIndex })
                return {
                  ...col,
                  id,
                  props: this.propsForWidget({ widget: col, id })
                }
              })
            }
          })
          .filter((row) => {
            // hide rows where all widgets are deregistered and configured as hidden
            return !row.cols.every((col) => {
              const deregisteredWidget = this.deregisteredWidgets[col.id]
              return deregisteredWidget && deregisteredWidget.info?.hideWidget
            })
          })
          .filter((row) => {
            return true
            // if hasResults is false, hide all rows where all widgets has hideWidgetWhenNoSurveyResults = true
          })
      }
    },
    showWidgets() {
      return this.viewMode === 'default'
    },
    widgetsForSlides() {
      // flat array from resultViews.rows[].cols
      let i = 0
      return this.resultViewWithWidgets.rows.reduce((acc, row) => {
        const cols = row.cols.map((col) => {
          return col
        })
        return acc.concat(cols)
      }, [])
    },
    allRegistrations() {
      // merge this.widgetRegistrations and this.deregisteredWidgets
      // return Object.keys(this.widgetRegistrations.widgets).reduce((acc, widgetId) => {
      //   acc =
      //   return acc
      // }, {})
      return {
        ...this.widgetRegistrations.widgets,
        ...this.deregisteredWidgets
      }
    }
  },
  watch: {
    selectedGroup() {
      this.registerWidgets()
    },
    selectedAttributeSegment() {
      this.registerWidgets()
    }
  },
  errorCaptured(err, vm, info) {
    // Handle the error and set hasError to true
    const id = vm.$.attrs.id
    const error = {
      error: err,
      stack: err.stack,
      id,
      info: info
    }
    this.widgetError[id] = error
    // Optionally log the error or perform other actions
    console.error('Error captured:', err, vm, info)
    // Returning false stops the error from propagating further
    this.onComponentError(error)
    return false
  },
  mounted() {
    this.registerWidgets()
  },
  methods: {
    didCloseSlideShow() {
      this.onChangeViewMode('default')
    },
    idForWidget({ col, rowIndex, colIndex }) {
      if (this.level > 0) {
        return `${col.type}-${rowIndex}-${colIndex}-level-${this.level}`
      }
      return `${col.type}-${rowIndex}-${colIndex}`
    },
    propsForWidget({ widget, id, fullScreen = false }) {
      // if (!this.widgetRegistrations.groupName !== this.selectedGroup.name) {
      //   return {}
      // }

      if (!this.widgetRegistrations?.widgets) {
        return {}
      }
      const thisWidget = this.widgetRegistrations.widgets[id]
      const colData = { data: widget.data }
      if (!thisWidget) {
        return colData
      }
      const props = Object.keys(thisWidget.requests).reduce(
        (acc, requestName) => {
          const request = thisWidget.requests[requestName]
          if (this.requestCallbacks[requestName]) {
            acc.data[requestName] = this.requestCallbacks[requestName]
          } else {
            const requestData = this.getDataForRequestFromStoresCallback({
              requestName,
              request
            })

            acc.data.hideWidgetWhenLoading = request.params?.hasOwnProperty(
              'hideWidgetWhenLoading'
            )
              ? request.params?.hideWidgetWhenLoading
              : true
            acc.data[requestName] = requestData.data
            acc.loading[requestName] = requestData.loading
            acc.error[requestName] = requestData.error
          }
          return acc
        },
        { data: {}, loading: {}, error: {} }
      )
      const widgetHasLoadingProp = Object.keys(props.loading).some(
        (key) => props.loading[key] !== false
      )
      const widgetHasLoadingError = Object.keys(props.error).some(
        (key) => props.error[key]
      )
      if (widget.type === 'wrapper') {
        props.data = {
          ...props.data,
          gridProps: { ...this.$props, level: this.level + 1 }
        }
      }
      if (widget.type === 'alert_box') {
        props.data = {
          ...props.data,
          userCanSelectAlertActions: this.userCanSelectAlertActions,
          userHasConfirmedActions: this.userHasConfirmedActions
        }
        if (this.userCanSelectAlertActions || this.userHasConfirmedActions) {
          props.data.filteredSurveyDetails = this.filteredSurveyDetails
        }
      }
      // common props
      props.data.selectedAttributeSegment = this.selectedAttributeSegment
      props.data.organizationName = this.organizationName
      props.data.selectedGroupName = this.selectedGroup.name
      props.data.selectedGroup = this.selectedGroup
      props.data.selectedGroupTitle = this.$tr(
        this.selectedGroup.messages,
        'title'
      )

      props.data.organizationFullName = this.organizationFullName
      props.data.userIsGroupAdmin = this.userIsGroupAdmin
      props.data.userIsPulsAdmin = this.userIsPulsAdmin
      props.data.userInfo = this.userInfo
      props.data.surveyIsAggregate = this.surveyIsAggregate
      props.data.surveyIsAggregateV2 = this.surveyIsAggregateV2
      props.data.surveyName = this.surveyName
      props.data.fullScreen = fullScreen
      props.data.theme = this.theme
      props.data.shouldShowResultsMessages = this.shouldShowResultsMessages

      props.data.viewMode = this.viewMode
      props.data.hasResults = this.hasResults
      return {
        ...props.data,
        ...colData,
        loading: widgetHasLoadingProp,
        error: widgetHasLoadingError
      }
    },
    registerWidgets() {
      const userIsParticipatingInSurvey = this.userInfo.groups.some(
        (userGroupName) =>
          this.surveyDetails.details.groups.find(
            (g) => g.name === userGroupName
          )
      )

      // loop over all rows and col and for each widget call onWidgetRegistration
      this.resultViewWithWidgets.rows.forEach((row, rowIndex) => {
        row.cols.forEach((col, colIndex) => {
          const doneWithAllWidgets =
            rowIndex === this.resultViewWithWidgets.rows.length - 1 &&
            colIndex === row.cols.length - 1
          const widget = this.widgetMap[col.type] || {
            widget: NotImplementedWidget
          }
          if (widget.information) {
            const information = widget.information({
              colData: col.data,
              userIsParticipatingInSurvey,
              userCanSelectAlertActions: this.userCanSelectAlertActions,
              userHasConfirmedActions: this.userHasConfirmedActions
            })
            this.onWidgetRegistration({
              id: col.id,
              information,
              data: col.data,
              doneWithAllWidgets
            })
          }
        })
      })
    },
    updateWidgetRequests({ id, requests, mode }) {
      this.updateWidgetRegistration({
        id,
        requests,
        mode
      })
    }
  }
}
</script>

<style lang="scss">
.widget-grid {
  max-width: 100%;
}
</style>
